/**
 * CalmBreathing — full-overlay wrapper that runs the internal breathing flow:
 *   TimeSelect → BreathingGuide → Complete
 *
 * A back/escape from TimeSelect, and Complete's "Done", both call onExit.
 * Self-contained: imports only the ported calm data/services/screens.
 *
 * `startImmediately` skips the TimeSelect step and drops straight into the guided
 * breathing at `presetMinutes` — used by the "Settle me" arrival shortcut, which
 * launches a fixed 2-minute panic reset with no picker. With no TimeSelect to fall
 * back to, a back/escape from the guide exits the whole flow.
 */
import { useEffect, useState } from 'react';
import { TimeSelect } from './breathing/TimeSelect';
import { BreathingGuide } from './breathing/BreathingGuide';
import { Complete } from './breathing/Complete';
import { getProtocol } from '@/data/calm/protocols';
import { destroy as destroyAudio } from '@/services/calm/audioController';
import './breathing/breathing.css';

type Phase = 'time' | 'guide' | 'complete';

export function CalmBreathing({
  protocolId,
  onExit,
  startImmediately = false,
  presetMinutes,
  showCareNext,
  onOpenCare,
  onSessionCompleted,
}: {
  protocolId: string;
  onExit: () => void;
  startImmediately?: boolean;
  presetMinutes?: number;
  showCareNext?: boolean;
  onOpenCare?: () => void;
  /** Optional: the timed session ran to its natural end (not ended early). */
  onSessionCompleted?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(startImmediately ? 'guide' : 'time');
  const [minutes, setMinutes] = useState(presetMinutes ?? 5);
  const [voice, setVoice] = useState('female');

  const protocol = getProtocol(protocolId);

  // Tear down the audio engine when leaving the flow entirely.
  useEffect(() => {
    return () => {
      destroyAudio();
    };
  }, []);

  return (
    <div className="cb-root">
      {phase === 'time' && (
        <TimeSelect
          protocol={protocol}
          stateId={protocol?.bestForStates[0]}
          onStart={({ minutes: m, voice: v }) => {
            setMinutes(m);
            setVoice(v);
            setPhase('guide');
          }}
          onBack={onExit}
        />
      )}

      {phase === 'guide' && (
        <BreathingGuide
          protocolId={protocolId}
          minutes={minutes}
          voice={voice}
          onExit={() => (startImmediately ? onExit() : setPhase('time'))}
          onComplete={() => setPhase('complete')}
          onNaturalComplete={onSessionCompleted}
        />
      )}

      {phase === 'complete' && <Complete onDone={onExit} showCareNext={showCareNext} onOpenCare={onOpenCare} />}
    </div>
  );
}
